import Big from "big.js";

function decimalAdjust(type, value, exp) {
  if (value === "--") {
    return "--";
  }
  if (typeof exp === "undefined" || +exp === 0) {
    return Math[type](value);
  }
  value = +value;
  exp = +exp;
  if (isNaN(value) || !(typeof exp === "number" && exp % 1 === 0)) {
    return NaN;
  }
  value = value.toString().split("e");
  value = Math[type](+(value[0] + "e" + (value[1] ? +value[1] - exp : -exp)));
  value = value.toString().split("e");
  return +(value[0] + "e" + (value[1] ? +value[1] + exp : exp));
}

const bigDecimalOp = (val1, val2, op) => {
  if (val1 === undefined || val2 === undefined) {
    return null;
  }

  let result;
  const n1 = new Big(val1);
  const n2 = new Big(val2);

  switch (op) {
    case "add":
      result = n1.plus(n2);
      break;
    case "subtract":
      result = n1.minus(n2);
      break;
    case "multiply":
      result = n1.times(n2);
      break;
    case "divide":
      try {
        result = n1.div(n2);
      } catch (error) {
        return null;
      }
      break;
    default:
      return null;
  }

  return parseFloat(result.toString());
};

const addCommasInPrice = (price, decimal = true) => {
  if (typeof price === "string") {
    const cleanedPrice = price.replace(/[^\d.]/g, "");
    if (!isNaN(cleanedPrice)) {
      return parseFloat(cleanedPrice).toLocaleString("en-IN", {
        maximumFractionDigits: decimal ? 2 : 0,
      });
    }
  } else if (!isNaN(price)) {
    return parseFloat(price).toLocaleString("en-IN", {
      maximumFractionDigits: decimal ? 2 : 0,
    });
  }
  return price;
};

const qrCodeRequired = process.env.REACT_APP_QRCODE_REQUIRED;

const lottieOptions = (animationData) => ({
  loop: true,
  autoplay: true,
  animationData,
  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
});

const onOpenDocument = (url, pdf) => {
  const left = window.screen.width / 2 - 400;
  const top = window.screen.height / 2 - 350;
  const newWindow = window.open(
    "",
    "",
    `width=800,height=700,left=${left},top=${top}`
  );

  if (newWindow) {
    if (pdf) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Document Preview</title>
          </head>
          <body style="margin:0">
            <iframe src="${url}" style="width:100%; height:100%; border:none;"></iframe>
          </body>
        </html>
      `);
    } else {
      newWindow.document.write(`
        <html>
          <head>
            <title>Document Preview</title>
          </head>
          <body>
            <img src="${url}" alt="doc" style="width:100%; height:auto;" />
          </body>
        </html>
      `);
    }
    newWindow.document.close();
  }
};

const inputAmount = (e, setAmount) => {
  if (e.target.value !== "" && e.target.value === 0) setAmount(0);
  else if (e.target.value.length === 2 && e.target.value[0] == 0)
    setAmount(e.nativeEvent.data);
  else if (e.target.value.length <= 8) setAmount(e.target.value);
};

const addingComma = (obj) => {
  const roundedObj = {};
  Object.keys(obj).forEach((key) => {
    roundedObj[key] = addCommasInPrice(obj[key]);
  });
  return roundedObj;
};

const comparisondata = {
  borderless: { price: 0.4, fees: 0.2, gst: 0.18, tcsper: 0.05 },
  wise: { price: 0.1, fees: 1.5, gst: 0.26, tcsper: 0.05002 },
  westernun: { price: 1, fees: 0.0, gst: 0.18, tcsper: 0.05 },
  flywire: { price: 1.2, fees: 0.1, gst: 0.18, tcsper: 0.05027 },
  other: { price: 1.5, fees: 0.5, gst: 0.18, tcsper: 0.05 },
};

const findExchangeRates = (amount, rate) => {
  const baseRate = rate;
  const exchangerates = {};
  const estimatedfees = {};
  const estimatedtcs = {};
  const estimatedgst = {};
  const difference = {};
  const total = {};
  Object.keys(comparisondata).forEach((provider, index) => {
    if (provider !== "currencies") {
      const { price, fees, gst, tcsper } = comparisondata[provider];
      exchangerates[provider] = bigDecimalOp(baseRate, price, "add");
      const multipliedValue = bigDecimalOp(
        exchangerates[provider],
        amount,
        "multiply"
      );
      estimatedfees[provider] =
        provider === "borderless"
          ? 1000
          : bigDecimalOp(
              bigDecimalOp(multipliedValue, fees, "multiply"),
              0.01,
              "multiply"
            );
      const taxableAmount = bigDecimalOp(multipliedValue, 700000, "subtract");
      const tcsValue = bigDecimalOp(taxableAmount, tcsper, "multiply");
      estimatedtcs[provider] = multipliedValue > 700000 ? tcsValue : 0;
      if (provider === "wise") {
        estimatedgst[provider] = bigDecimalOp(
          estimatedfees[provider],
          gst,
          "multiply"
        );
      } else {
        let calcvalue;
        if (multipliedValue <= 100000)
          calcvalue = Math.max(
            bigDecimalOp(0.01, multipliedValue, "multiply"),
            250
          );
        else if (multipliedValue > 100000 && multipliedValue <= 1000000)
          calcvalue = bigDecimalOp(
            bigDecimalOp(
              bigDecimalOp(multipliedValue, 100000, "subtract"),
              0.005,
              "multiply"
            ),
            1000,
            "add"
          );
        else
          calcvalue = Math.max(
            bigDecimalOp(
              bigDecimalOp(
                bigDecimalOp(multipliedValue, 1000000, "subtract"),
                0.001,
                "multiply"
              ),
              5500,
              "add"
            ),
            60000
          );
        estimatedgst[provider] = bigDecimalOp(calcvalue, gst, "multiply");
      }
      total[provider] = Math.round(
        bigDecimalOp(
          bigDecimalOp(
            bigDecimalOp(multipliedValue, estimatedfees[provider], "add"),
            estimatedtcs[provider],
            "add"
          ),
          estimatedgst[provider],
          "add"
        )
      );
      difference[provider] = bigDecimalOp(
        total[provider],
        total["borderless"],
        "subtract"
      );
    }
  });
  const totalamount = addingComma(total);
  return { totalamount, difference };
};

export {
  decimalAdjust,
  addCommasInPrice,
  qrCodeRequired,
  bigDecimalOp,
  lottieOptions,
  onOpenDocument,
  inputAmount,
  comparisondata,
  findExchangeRates,
};
